// ImportCVDialog.tsx — Import any native CV file (.cvp / .cvt / .cvpanel / .cvsite)
// Detects file type from envelope, handles project name conflicts with Replace / Create New choice.

import { useRef, useState, useEffect } from 'react';
import DialogBase from './DialogBase';
import MaterialListTable from '../components/MaterialListTable';
import { useShallowStore } from '../store/useStore';
import { parseCVFile } from '../utils/cvpFormat';
import type { CVProjectEnvelope, CVTemplateEnvelope, CVPanelEnvelope, CVSiteEnvelope } from '../utils/cvpFormat';
import { mergePanelDefaults } from '../types/panelvar';
import type { PanelData, CVProject, SiteData } from '../types/panelvar';

type Stage =
  | { kind: 'idle' }
  | { kind: 'error';    message: string }
  | { kind: 'project';  envelope: CVProjectEnvelope; conflictName: string | null }
  | { kind: 'template'; envelope: CVTemplateEnvelope }
  | { kind: 'panel';    envelope: CVPanelEnvelope }
  | { kind: 'site';     envelope: CVSiteEnvelope }
  | { kind: 'cad-import'; panels: PanelData[]; warnings: string[] }
  | { kind: 'cad-project'; project: CVProject; warnings: string[]; conflictName: string | null }
  | { kind: 'cad-site'; site: SiteData; warnings: string[] };

export default function ImportCVDialog() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage]           = useState<Stage>({ kind: 'idle' });
  const [importMode, setImportMode] = useState<'replace' | 'new'>('new');

  const {
    project,
    projectList,
    ui,
    refreshProjectList,
    importCVProject,
    importCVPanel,
    importCVSite,
    importCVTemplate,
  } = useShallowStore(s => ({
    project:           s.project,
    projectList:       s.projectList,
    ui:                s.ui,
    refreshProjectList: s.refreshProjectList,
    importCVProject:   s.importCVProject,
    importCVPanel:     s.importCVPanel,
    importCVSite:      s.importCVSite,
    importCVTemplate:  s.importCVTemplate,
  }));

  const isMetric = ui.units === 'metric';

  // Ensure project list is fresh for conflict detection
  useEffect(() => { refreshProjectList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text !== 'string') {
        setStage({ kind: 'error', message: 'Could not read file.' });
        return;
      }
      const result = parseCVFile(text);
      if (!result.ok) {
        setStage({ kind: 'error', message: result.error });
        return;
      }
      // cv-cad named export — return panels + warnings directly
      if ('cadImport' in result) {
        setStage({ kind: 'cad-import', panels: result.panels, warnings: result.warnings });
        return;
      }
      // cv-cad project export — return project + warnings
      if ('cadProjectImport' in result) {
        const conflict = projectList.find(p => p.name === result.project.name) ?? null;
        setStage({ kind: 'cad-project', project: result.project, warnings: result.warnings, conflictName: conflict?.name ?? null });
        setImportMode('new');
        return;
      }
      // cv-cad site export — return site + warnings
      if ('cadSiteImport' in result) {
        setStage({ kind: 'cad-site', site: result.site, warnings: result.warnings });
        return;
      }
      const env = result.envelope;
      if (env.type === 'project') {
        const conflict = projectList.find(p => p.name === env.project.name) ?? null;
        setStage({ kind: 'project', envelope: env, conflictName: conflict?.name ?? null });
        setImportMode('new');
      } else if (env.type === 'template') {
        setStage({ kind: 'template', envelope: env });
      } else if (env.type === 'panel') {
        setStage({ kind: 'panel', envelope: env });
      } else {
        setStage({ kind: 'site', envelope: env });
      }
    };
    reader.onerror = () => setStage({ kind: 'error', message: 'Error reading file.' });
    reader.readAsText(file);
    e.target.value = ''; // reset so re-selecting same file triggers onChange
  }

  function handleImport() {
    switch (stage.kind) {
      case 'project':
        importCVProject(stage.envelope.project, importMode);
        break;
      case 'template':
        importCVTemplate(stage.envelope.template);
        break;
      case 'panel':
        importCVPanel(stage.envelope.panel);
        break;
      case 'site':
        importCVSite(stage.envelope.site);
        break;
      case 'cad-import':
        // Import each panel from the cv-cad export
        for (const panel of stage.panels) {
          importCVPanel(panel);
        }
        break;
      case 'cad-project':
        importCVProject(stage.project, importMode);
        break;
      case 'cad-site':
        importCVSite(stage.site);
        break;
      default:
        return; // idle / error — DialogBase closes, nothing imported
    }
  }

  const canImport = stage.kind !== 'idle' && stage.kind !== 'error';

  return (
    <DialogBase
      title="Import CV File"
      okLabel={canImport ? 'Import' : 'Close'}
      onOk={canImport ? handleImport : undefined}
    >
      {/* File picker */}
      <div className="dialog-row" style={{ marginBottom: 12 }}>
        <input
          ref={fileRef}
          type="file"
          accept=".cvp,.cvt,.cvpanel,.cvsite,.json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button className="dialog-btn" onClick={() => fileRef.current?.click()}>
          Choose File…
        </button>
      </div>

      {/* States */}
      {stage.kind === 'idle' && (
        <p className="dialog-info">
          Select a <strong>.cvp</strong>, <strong>.cvt</strong>, <strong>.cvpanel</strong>,
          or <strong>.cvsite</strong> file to import.
        </p>
      )}

      {stage.kind === 'error' && (
        <p className="dialog-warning">{stage.message}</p>
      )}

      {stage.kind === 'project' && (
        <>
          <p className="dialog-info">
            <strong>Project:</strong> {stage.envelope.project.name}
          </p>
          <p className="dialog-info">
            {stage.envelope.project.panels.length} panel(s)
            · exported {stage.envelope.exportedAt.slice(0, 10)}
          </p>
          {stage.conflictName ? (
            <>
              <p className="dialog-warning">
                A project named &ldquo;{stage.conflictName}&rdquo; already exists.
              </p>
              <div className="dialog-row" style={{ gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="import-mode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                  />
                  Replace existing
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="import-mode"
                    value="new"
                    checked={importMode === 'new'}
                    onChange={() => setImportMode('new')}
                  />
                  Create as new
                </label>
              </div>
            </>
          ) : (
            <p className="dialog-info">Project will be imported as a new project.</p>
          )}
          <MaterialListTable panels={stage.envelope.project.panels.map(mergePanelDefaults)} isMetric={isMetric} />
        </>
      )}

      {stage.kind === 'template' && (
        <>
          <p className="dialog-info">
            <strong>Template:</strong> {stage.envelope.template.name}
          </p>
          <p className="dialog-info">
            {stage.envelope.template.panels.length} panel archetype(s)
            · exported {stage.envelope.exportedAt.slice(0, 10)}
          </p>
          <p className="dialog-info">
            A new project will be created with blank job info. Fill in job details after import.
          </p>
          <MaterialListTable panels={stage.envelope.template.panels.map(mergePanelDefaults)} isMetric={isMetric} />
        </>
      )}

      {stage.kind === 'panel' && (
        <>
          <p className="dialog-info">
            <strong>Panel:</strong> {stage.envelope.panel.mpvar.panelMark}
            · exported {stage.envelope.exportedAt.slice(0, 10)}
          </p>
          <p className="dialog-info">
            {project
              ? <>Panel will be added to the current project: <strong>{project.name}</strong>.</>
              : <>A new project will be created to hold this panel.</>}
          </p>
          <MaterialListTable panels={[mergePanelDefaults(stage.envelope.panel)]} isMetric={isMetric} />
        </>
      )}

      {stage.kind === 'site' && (
        <>
          <p className="dialog-info">
            <strong>Site plan</strong> · exported {stage.envelope.exportedAt.slice(0, 10)}
          </p>
          <p className="dialog-info">
            {project
              ? (project.site
                  ? `This will replace the existing site layout in "${project.name}".`
                  : `Site plan will be added to "${project.name}".`)
              : 'A new project will be created to hold this site plan.'}
          </p>
        </>
      )}

      {stage.kind === 'cad-import' && (
        <>
          <p className="dialog-info">
            <strong>CV-CAD Export</strong> — {stage.panels.length} panel(s) detected
          </p>
          <p className="dialog-info">
            {project
              ? <>Panel(s) will be added to the current project: <strong>{project.name}</strong>.</>
              : <>A new project will be created to hold these panel(s).</>}
          </p>
          {stage.warnings.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <p className="dialog-warning" style={{ marginBottom: 4 }}>
                {stage.warnings.length} warning(s) — some fields could not be mapped:
              </p>
              <ul style={{ maxHeight: 160, overflowY: 'auto', fontSize: '0.85em', margin: 0, paddingLeft: 18 }}>
                {stage.warnings.map((w, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>{w}</li>
                ))}
              </ul>
              <p className="dialog-info" style={{ marginTop: 6, fontStyle: 'italic' }}>
                Partial import — unrecognized fields are skipped. You can still import.
              </p>
            </div>
          )}
          <MaterialListTable panels={stage.panels.map(mergePanelDefaults)} isMetric={isMetric} />
        </>
      )}

      {stage.kind === 'cad-project' && (
        <>
          <p className="dialog-info">
            <strong>CV-CAD Project:</strong> {stage.project.name}
          </p>
          <p className="dialog-info">
            Client: {stage.project.client || '(none)'}
            {stage.project.address ? ` · ${stage.project.address}` : ''}
          </p>
          {stage.conflictName ? (
            <>
              <p className="dialog-warning">
                A project named &ldquo;{stage.conflictName}&rdquo; already exists.
              </p>
              <div className="dialog-row" style={{ gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="import-mode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                  />
                  Replace existing
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="import-mode"
                    value="new"
                    checked={importMode === 'new'}
                    onChange={() => setImportMode('new')}
                  />
                  Create as new
                </label>
              </div>
            </>
          ) : (
            <p className="dialog-info">Project will be created from CV-CAD title block data.</p>
          )}
          {stage.warnings.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <ul style={{ maxHeight: 160, overflowY: 'auto', fontSize: '0.85em', margin: 0, paddingLeft: 18 }}>
                {stage.warnings.map((w, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          <MaterialListTable panels={stage.project.panels.map(mergePanelDefaults)} isMetric={isMetric} />
        </>
      )}

      {stage.kind === 'cad-site' && (
        <>
          <p className="dialog-info">
            <strong>CV-CAD Site Plan:</strong> {stage.site.projectName || '(unnamed)'}
          </p>
          <p className="dialog-info">
            Grid spacing: {stage.site.gridSpacing > 0 ? `${stage.site.gridSpacing} ft` : 'N/A'}
            · {stage.site.panels.length} panel placement(s) extracted
          </p>
          <p className="dialog-info">
            {project
              ? (project.site
                  ? `This will replace the existing site layout in "${project.name}".`
                  : `Site plan will be added to "${project.name}".`)
              : 'A new project will be created to hold this site plan.'}
          </p>
          {stage.warnings.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <ul style={{ maxHeight: 160, overflowY: 'auto', fontSize: '0.85em', margin: 0, paddingLeft: 18 }}>
                {stage.warnings.map((w, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </DialogBase>
  );
}
